import type { BulkImportRightPanelMode, BulkUserImportDraft } from '@/types/bulkUserImport';
import type { WorkflowProgressStep, WorkflowUiPhase } from '@/components/workflow/types';

/** Steps 3–4 ("Adding everyone…", "Checking details…") — 0-based index 2+. */
const SKELETON_START_STEP_INDEX = 2;

function shouldShowBulkImportSkeleton(progressRail: WorkflowProgressStep[]): boolean {
  if (progressRail.length === 0) return false;

  const inProgressIdx = progressRail.findIndex((s) => s.status === 'in-progress');
  if (inProgressIdx >= SKELETON_START_STEP_INDEX) return true;

  return progressRail.every((s) => s.status === 'completed');
}

/** No-CSV flow: upload skeleton after loading, then upload UI in form. */
function getNoCsvPanelMode(
  uiPhase: WorkflowUiPhase,
  progressRail: WorkflowProgressStep[]
): BulkImportRightPanelMode {
  if (uiPhase === 'working') {
    const step = progressRail[0];
    if (!step || step.status === 'pending' || step.status === 'in-progress') return 'hidden';
    return 'upload-skeleton';
  }

  if (uiPhase === 'almost-ready') return 'upload-skeleton';

  if (uiPhase === 'form' || uiPhase === 'saving' || uiPhase === 'success') {
    return 'empty-upload';
  }

  return 'hidden';
}

export function getBulkImportRightPanelMode(
  uiPhase: WorkflowUiPhase,
  draft: BulkUserImportDraft,
  progressRail: WorkflowProgressStep[] = [],
  csvProcessing = false
): BulkImportRightPanelMode {
  if (uiPhase === 'idle' || uiPhase === 'thinking') return 'hidden';

  if (!draft.hasCsvAtSend) {
    if (csvProcessing) return 'skeleton';
    if (draft.rows.length > 0) {
      if (uiPhase === 'saving') return 'skeleton';
      if (uiPhase === 'form' || uiPhase === 'success') return 'data';
      return 'hidden';
    }
    return getNoCsvPanelMode(uiPhase, progressRail);
  }

  if (uiPhase === 'working' || uiPhase === 'almost-ready') {
    return shouldShowBulkImportSkeleton(progressRail) ? 'skeleton' : 'hidden';
  }

  if (uiPhase === 'saving') {
    if (csvProcessing) return 'skeleton';
    return draft.rows.length > 0 ? 'skeleton' : 'empty-upload';
  }

  if (uiPhase === 'form' || uiPhase === 'success') {
    if (csvProcessing) return 'skeleton';
    return draft.rows.length > 0 ? 'data' : 'empty-upload';
  }

  return 'hidden';
}
